import { appSettings } from 'AppSettings';
import { PatientReviewScore, PatientReview, ReviewStatus } from './PatientReviewData';
import { BaseGet, BaseGetWithParams } from './BaseData';
import { MeasurementNormality } from './MeasurementDefinitionData';
import assertExhaustive from '../researchProjectManagement/patientApproval/utils/assertExhaustive';

export interface PatientApprovalPatient {
  researchProjectId: string;
  deidentity: string;
  yearOfBirth?: number;
  state?: string;
  sampleBiobanks: string[];

  statisticTotalCount: number;

  labTotalCount: number;
  labCuratedTotalCount: number;
  labTotalWithIssuesCount: number;
  labCuratedTotalWithIssuesCount: number;
  labTotalWithoutIssuesCount: number;
  labCuratedTotalWithoutIssuesCount: number;

  obsTotalCount: number;
  obsCuratedTotalCount: number;
  obsTotalWithIssuesCount: number;
  obsCuratedTotalWithIssuesCount: number;
  obsTotalWithoutIssuesCount: number;
  obsCuratedTotalWithoutIssuesCount: number;

  medTotalCount: number;
  medTotalWithIssuesCount: number;
  medTotalWithoutIssuesCount: number;

  procTotalCount: number;
  procTotalWithIssuesCount: number;
  procTotalWithoutIssuesCount: number;

  problemTotalCount: number;
  problemTotalWithIssuesCount: number;
  problemTotalWithoutIssuesCount: number;

  reviewStatusId: ReviewStatus['reviewStatusId'] | null;
  reviewScore: PatientReviewScore | null;
  reviewNotes: string | null;
}

export interface PatientApprovalPatientTest {
  test: string;
  type: string;
  codes: string[];
}

export interface PatientApprovalPatientWeeklyCount {
  performedWeek: string;
  type: string;
  totalCount: number;
  noIssuesCount: number;
}

export interface PatientApprovalPatientMeasurement {
  type: string;
  performedWeek: string;
  test: string;
  codes: string[];
  average: number;
  units: string;
  normality: MeasurementNormality;
  issues: string[];
}

export interface PatientApprovalEvent {
  name: string;
  eventDate: string;
  issues: string[];
}

export interface PatientApprovalSampleCollectionDate {
  sampleCount: number;
  collectionDate: string;
}

export const PATIENT_JOURNEY_ENTITIES = [
  'problem',
  'observation',
  'medication',
  'procedure',
  'lab',
  'pathReport',
] as const;
export type PatientJourneyEntity = (typeof PATIENT_JOURNEY_ENTITIES)[number];

export const getPatientJourneyEntityDisplayName = (entity: PatientJourneyEntity) => {
  switch (entity) {
    case 'lab':
      return 'Labs';
    case 'medication':
      return 'Medications';
    case 'observation':
      return 'Observations';
    case 'problem':
      return 'Problems';
    case 'procedure':
      return 'Procedures';
    case 'pathReport':
      return 'Path Reports';
    default:
      assertExhaustive(entity);
  }
};

export interface PatientJourneyRow {
  id: string;
  date: string;
  entity: PatientJourneyEntity;
  vocabulary: string | null;
  vocabularyCode: string | null;
  name: string;
  result: string | null;
  issues: string[];
}

export interface PatientApprovalPatientData {
  tests: PatientApprovalPatientTest[];
  weeklyCounts: PatientApprovalPatientWeeklyCount[];
  measurements: PatientApprovalPatientMeasurement[];
  events: PatientApprovalEvent[];
  sampleCollectionDates: PatientApprovalSampleCollectionDate[];
  patientJourney: PatientJourneyRow[];
  review: PatientReview | null;
}

export async function GetResearchProjectApprovalPatients(
  researchProjectId: string,
  accessToken: string | undefined
): Promise<PatientApprovalPatient[] | undefined> {
  return await BaseGet(
    `${appSettings.api.endpoint}/api/v1/PatientApproval/ResearchProject/${researchProjectId}/Patients`,
    accessToken
  );
}

export async function GetResearchProjectPatientData(
  researchProjectId: string,
  deidentity: string,
  accessToken: string | undefined
): Promise<PatientApprovalPatientData | undefined> {
  return await BaseGetWithParams(
    `${appSettings.api.endpoint}/api/v1/PatientApproval/ResearchProject/${researchProjectId}/Patient`,
    new URLSearchParams({ deidentity }),
    accessToken
  );
}
